import {
  Component,
  ElementRef,
  HostListener,
  inject,
  input,
  output,
  viewChild,
  signal,
  computed,
  OnInit,
  AfterViewInit,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { DomSanitizer, SafeHtml, SafeResourceUrl } from '@angular/platform-browser';
import { MatIconModule } from '@angular/material/icon';

import { ChatMessagesList, HeaderButton, KeyValueSelect, ChatTexts, CarouselButtonClick } from '../interfaces';
import { SoSvgIconUI } from '../so-svg-icon/so-svg-icon.component';
import { SoEmojisUI } from '../so-emojis-ui/so-emojis-ui.component';
import { CarouselMessageUI } from './components/carousel-message/carousel-message.component';

@Component({
  selector: 'so-chat-whatsapp-ui',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    SoSvgIconUI,
    SoEmojisUI,
    CarouselMessageUI
  ],
  templateUrl: './so-chat-whatsapp-ui.component.html',
  styleUrl: './so-chat-whatsapp-ui.component.scss'
})
export class SoChatWhatsappUI implements OnInit, AfterViewInit, OnChanges {
  contactName = input.required<string>();
  conversations = input<ChatMessagesList[] | undefined>(undefined);
  contactNamePosition = input<'left' | 'right'>('left');
  showHeader = input<boolean>(true);
  showFooter = input<boolean>(true);
  footerMessage = input<string>('');
  footerMessageSeparator = input<string>('');
  itemsVariables = input<KeyValueSelect[]>([]);
  isLoading = input<boolean>(false);
  showButtonBarActions = input<boolean>(false);
  showFooterMessage = input<boolean>(false);
  showEmojis = input<boolean>(false);
  showVariables = input<boolean>(false);
  showAttachImages = input<boolean>(false);
  showBtnRmImage = input<boolean>(false);
  disabledButtonsAction = input<boolean>(false);
  showButtonFullscreen = input<boolean>(false);
  initialScroll = input<boolean>(true);
  autoScroll = input<boolean>(true);
  emojiDisplayPosition = input<'top' | 'left' | 'right'>('top');
  maxFileSizeMB = input<number>(5);
  allowedAttachmentExtensions = input<string[] | null>(null);
  excludedEmojis = input<string[]>([]);
  headerButton = input<HeaderButton | undefined>(undefined);
  enableCarousel = input<boolean>(false);
  enableOptIn = input<boolean>(false);
  textStyle = input<string | undefined>(undefined);
  texts = input<ChatTexts>({
    today: 'Hoy',
    yesterday: 'Ayer',
    noMessages: 'Aún no hay mensajes',
    loadingMessages: 'Cargando mensajes...',
    photos: 'Fotos',
    variables: 'Variables',
    transcribe: 'Transcribir',
    errorImageOnly: 'Solo se permiten archivos de imagen.',
    errorFileTooLarge: 'El archivo excede el tamaño máximo permitido de'
  });
  avatarUrl = input<string>('img/mdi_avatar3.png');
  backgroundImageUrl = input<string>('img/bg-whatsapp.png');

  varSelected = output<string>();
  emojiSelected = output<string>();
  headerButtonClicked = output<void>();
  carouselButtonClicked = output<CarouselButtonClick>();
  onPhotoSelected = output<File>();
  isFullscreen = output<boolean>();
  removeImage = output<void>();
  onError = output<{ title: string; message: string }>();

  chatBottom = viewChild<ElementRef>('chatBottom');
  popoverButton = viewChild<ElementRef<HTMLButtonElement>>('popoverButton');
  fileInput = viewChild<ElementRef<HTMLInputElement>>('fileInput');

  showPopover = signal<boolean>(false);
  activePopoverView = signal<'default' | 'variables' | 'photos'>('default');
  theme = signal<string>('light');
  isPlaying = signal<Record<string, boolean>>({});
  progress = signal<Record<string, number>>({});
  audioDuration = signal<Record<string, string>>({});

  private setIsFullscreen = false;
  private readonly sanitizer = inject(DomSanitizer);

  footerMessagePart1 = signal<string>('');
  footerMessagePart2 = signal<string>('');
  footerMessagePart3 = signal<string>('');

  gridTemplateRows = computed(() => {
    const header = this.showHeader() ? '50px ' : '';
    const footer = this.showFooter() ? ' 50px' : '';
    return `${header}auto${footer}`;
  });

  backgroundImageStyle = computed(() => {
    const url = this.backgroundImageUrl();
    return url ? `url('${url}')` : 'none';
  });

  ngOnInit(): void {
    const footerMsg = this.footerMessage();
    const separator = this.footerMessageSeparator();
    if (!footerMsg || !separator) return;

    const regEx = new RegExp(separator, 'i');
    const separatorMatch = footerMsg.match(regEx);
    if (!separatorMatch || separatorMatch.length === 0) return;

    const footerMessageSplited = footerMsg.split(regEx, 2);
    if (footerMessageSplited.length !== 2) return;

    this.footerMessagePart1.set(footerMessageSplited[0].trim());
    this.footerMessagePart2.set(separatorMatch[0]);
    this.footerMessagePart3.set(footerMessageSplited[1].trim());
  }

  ngAfterViewInit(): void {
    const html = document.getElementsByTagName('html')[0];
    if (!html) return;
    this.theme.set(html.dataset['theme']?.trim().toLowerCase() ?? 'light');
    new MutationObserver(() => {
      this.theme.set(html.dataset['theme']?.trim().toLowerCase() ?? this.theme());
    }).observe(html, { attributeFilter: ['data-theme'] });

    if (this.initialScroll()) {
      this.scrollToBottom();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['conversations'] && this.autoScroll()) {
      this.scrollToBottom();
    }
  }

  isTodayOrYesterday(date: string): string | null {
    const inputDate = new Date(date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    inputDate.setHours(0, 0, 0, 0);

    const t = this.texts();
    if (inputDate.getTime() === today.getTime()) {
      return t.today ?? 'Hoy';
    } else if (inputDate.getTime() === yesterday.getTime()) {
      return t.yesterday ?? 'Ayer';
    } else {
      const datePipe = new DatePipe('es-ES');
      return datePipe.transform(inputDate, 'EEE d \'de\' MMMM');
    }
  }

  containtGoogleUrl(text: string): boolean {
    const googleMapsRegex = /https:\/\/maps\.app\.goo\.gl\/[A-Za-z0-9]+/;
    return googleMapsRegex.test(text);
  }

  sanitizeUrl(url: string): SafeResourceUrl {
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  splitMessage(message: string): string[] {
    const urlRegex = /(https:\/\/maps\.app\.goo\.gl\/[A-Za-z0-9]+)/;
    return message.split(urlRegex);
  }

  scrollToBottom(): void {
    const bottom = this.chatBottom();
    if (bottom) {
      setTimeout(() => {
        bottom.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }, 500);
    }
  }

  onCarouselButtonClick(event: CarouselButtonClick): void {
    this.carouselButtonClicked.emit(event);
  }

  onIframeLoaded(isloaded: boolean): void {
    if (isloaded) {
      this.scrollToBottom();
    }
  }

  cleanMessage(message: string): string {
    let raw = message.replace(/\+/g, ' ');

    try {
      for (let i = 0; i < 3; i++) {
        const decoded = decodeURIComponent(raw);
        if (decoded === raw) break;
        raw = decoded;
      }
    } catch { /* swallow */ }

    let cleanedMessage = raw.replace(
      /U\+((?:10[0-9A-F]{4}|[0-9A-F]{4,5}))/gi,
      (m, hex) => {
        const cp = parseInt(hex, 16);
        return cp <= 0x10FFFF ? String.fromCodePoint(cp) : m;
      }
    );

    cleanedMessage = cleanedMessage.replace(/\\n/g, '\n');
    cleanedMessage = cleanedMessage.replace(/(\r\n|\n|\r)/g, '<br>');
    cleanedMessage = cleanedMessage.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    cleanedMessage = cleanedMessage.trim();

    return `<p>${cleanedMessage}</p>`;
  }

  getSafeMessage(message: string): SafeHtml {
    const cleaned = this.cleanMessage(message);
    return this.sanitizer.bypassSecurityTrustHtml(cleaned);
  }

  togglePopover(event: MouseEvent): void {
    event.stopPropagation();
    this.showPopover.update(v => !v);
    this.activePopoverView.set('default');
  }

  switchPopover(view: 'variables' | 'photos'): void {
    this.showPopover.set(false);
    setTimeout(() => {
      this.activePopoverView.set(view);
      this.showPopover.set(true);
    }, 200);
  }

  selectVariable(value: string): void {
    this.varSelected.emit(value);
  }

  addEmoji(nuevoEmoji: string): void {
    this.emojiSelected.emit(nuevoEmoji);
  }

  handlePhotoUpload(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      const isAllowedByType = this.isFileTypeAllowed(file);
      const maxBytes = this.maxFileSizeMB() * 1024 * 1024;
      const t = this.texts();

      if (!isAllowedByType) {
        const allowedTypesLabel = this.allowedTypesLabel();
        this.onError.emit({
          title: 'Error',
          message: allowedTypesLabel
            ? `Solo se permiten archivos tipo: ${allowedTypesLabel}.`
            : (t.errorImageOnly ?? 'Solo se permiten archivos de imagen.')
        });
        input.value = '';
        return;
      }

      if (file.size > maxBytes) {
        this.onError.emit({
          title: 'Error',
          message: `${t.errorFileTooLarge ?? 'El archivo excede el tamaño máximo permitido de'} ${this.maxFileSizeMB()}MB.`
        });
        input.value = '';
        return;
      }

      this.onPhotoSelected.emit(file);
      input.value = '';
      this.showPopover.set(false);
    }
  }

  fileInputAccept(): string | null {
    const normalizedExtensions = this.normalizedAllowedExtensions();
    if (!normalizedExtensions.length) return 'image/*';
    return normalizedExtensions.map(ext => `.${ext}`).join(',');
  }

  private normalizedAllowedExtensions(): string[] {
    const allowed = this.allowedAttachmentExtensions();
    if (!allowed?.length) return [];
    const uniqueExtensions = new Set(
      allowed
        .map(ext => (ext ?? '').toString().trim().toLowerCase().replace(/^\./, ''))
        .filter(ext => ext.length > 0)
    );
    return Array.from(uniqueExtensions);
  }

  private allowedTypesLabel(): string {
    return this.normalizedAllowedExtensions().join(', ');
  }

  private isFileTypeAllowed(file: File): boolean {
    const allowedExtensions = this.normalizedAllowedExtensions();
    if (!allowedExtensions.length) return file.type.startsWith('image/');

    const fileExtension = file.name.split('.').pop()?.toLowerCase() ?? '';
    if (allowedExtensions.includes(fileExtension)) return true;

    const mimeExtension = file.type?.split('/').pop()?.toLowerCase() ?? '';
    return allowedExtensions.includes(mimeExtension);
  }

  triggerFileInput(): void {
    const input = this.fileInput();
    if (input) {
      input.nativeElement.click();
    }
  }

  changeScreen(): void {
    this.setIsFullscreen = !this.setIsFullscreen;
    this.isFullscreen.emit(this.setIsFullscreen);
  }

  resetScreenSize(value: boolean): void {
    this.setIsFullscreen = value;
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event): void {
    if (!event.target || !(event.target as HTMLElement).closest('.attach-btn, .popover')) {
      this.showPopover.set(false);
    }
  }

  toggleAudio(messageID: string): void {
    const audio = document.getElementById(`audio-${messageID}`) as HTMLAudioElement;

    const currentPlaying = this.isPlaying();
    Object.keys(currentPlaying).forEach(id => {
      if (id !== messageID && currentPlaying[id]) {
        const otherAudio = document.getElementById(`audio-${id}`) as HTMLAudioElement;
        if (otherAudio) {
          otherAudio.pause();
          this.isPlaying.update(state => ({ ...state, [id]: false }));
        }
      }
    });

    if (audio.paused) {
      audio.play();
      this.isPlaying.update(state => ({ ...state, [messageID]: true }));
    } else {
      audio.pause();
      this.isPlaying.update(state => ({ ...state, [messageID]: false }));
    }
  }

  setDuration(messageID: string): void {
    const audio = document.getElementById(`audio-${messageID}`) as HTMLAudioElement;
    if (audio && !isNaN(audio.duration) && audio.duration > 0) {
      this.audioDuration.update(state => ({
        ...state,
        [messageID]: this.formatTime(audio.duration)
      }));
    }
  }

  updateProgress(event: Event, messageID: string): void {
    const audio = event.target as HTMLAudioElement;
    this.progress.update(state => ({
      ...state,
      [messageID]: (audio.currentTime / audio.duration) * 100
    }));

    const timeRemaining = audio.duration - audio.currentTime;
    this.audioDuration.update(state => ({
      ...state,
      [messageID]: this.formatTime(timeRemaining)
    }));
  }

  onAudioEnded(messageID: string): void {
    this.isPlaying.update(state => ({ ...state, [messageID]: false }));
    this.progress.update(state => ({ ...state, [messageID]: 0 }));
    this.audioDuration.update(state => ({ ...state, [messageID]: this.formatTime(0) }));
  }

  seekAudio(event: MouseEvent, messageID: string): void {
    const audio = document.getElementById(`audio-${messageID}`) as HTMLAudioElement;
    const progressBar = event.currentTarget as HTMLElement;
    const clickPosition = event.offsetX / progressBar.offsetWidth;
    audio.currentTime = clickPosition * audio.duration;
  }

  formatTime(time: number): string {
    if (isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60).toString().padStart(2, '0');
    return `${minutes}:${seconds}`;
  }

  toggleTranscription(message: { showTranscription?: boolean }): void {
    message.showTranscription = !message.showTranscription;
  }

  onClickRemoveImage(): void {
    this.removeImage.emit();
  }

  onClickHeaderButton(): void {
    this.headerButtonClicked.emit();
  }
}
